import { Link, useLocation } from 'react-router-dom';
import { Phone, Mail, MapPin, ShieldCheck, ArrowUpRight } from 'lucide-react';
import { getLanguageFromPath } from '../lib/i18n';

export default function Footer() {
  const currentYear = new Date().getFullYear();
  const location = useLocation();
  const currentLang = getLanguageFromPath(location.pathname);

  const FOOTER_DATA = {
    da: {
      homeHref: '/',
      brandText: 'Danshaker er den originale, danskudviklede og danskproducerede sorteringsmaskine. Vi hjælper dig med at spare tid, strøelse og skåne kroppen i det daglige sorteringsarbejde.',
      col1Title: 'Maskinen',
      col2Title: 'Finansiering',
      col3Title: 'Info',
      col4Title: 'Kontakt os',
      demoButton: 'Book gratis demo',
      demoHref: '/gratis-demo',
      copyright: `© ${currentYear} Danshaker. Alle rettigheder forbeholdes.`,
      developedBy: 'Udviklet i Danmark af',
      links: {
        maskinen: [
          { label: 'Produkt', href: '/produkt' },
          { label: 'Tilbehør', href: '/tilbehoer' },
          { label: 'Besparelsesberegner', href: '/beregner' },
          { label: 'Service', href: '/service' }
        ],
        finansiering: [
          { label: 'Leje', href: '/leje' },
          { label: 'Erhvervsleasing', href: '/erhvervsleasing' },
          { label: 'Finansiering til private', href: '/privatfinansiering' }
        ],
        omOs: [
          { label: 'Om Danshaker', href: '/om-danshaker' },
          { label: 'Mød os her / events', href: '/moed-os-her' },
          { label: 'Nyheder', href: '/nyheder' },
          { label: 'Kundeudtalelser', href: '/kundeudtalelser' },
          { label: 'FAQ', href: '/faq' }
        ]
      }
    },
    en: {
      homeHref: '/en',
      brandText: 'Danshaker is the original Danish-developed and Danish-manufactured sorting machine. We help you save time, bedding, and protect your physical health in daily sorting work.',
      col1Title: 'The Machine',
      col2Title: 'Financing',
      col3Title: 'About us',
      col4Title: 'Contact us',
      demoButton: 'Book free demo',
      demoHref: '/en/free-demo',
      copyright: `© ${currentYear} Danshaker. All rights reserved.`,
      developedBy: 'Developed in Denmark by',
      links: {
        maskinen: [
          { label: 'Product', href: '/en/product' },
          { label: 'Accessories', href: '/en/accessories' },
          { label: 'Savings Calculator', href: '/en/calculator' },
          { label: 'Service', href: '/en/service' }
        ],
        finansiering: [
          { label: 'Rental', href: '/en/rent' },
          { label: 'Commercial Leasing', href: '/en/commercial-leasing' },
          { label: 'Private Financing', href: '/en/private-financing' }
        ],
        omOs: [
          { label: 'About Danshaker', href: '/en/about-danshaker' },
          { label: 'Meet Us / Events', href: '/en/meet-us' },
          { label: 'News', href: '/en/news' },
          { label: 'Reviews', href: '/en/reviews' },
          { label: 'FAQ', href: '/en/faq' }
        ]
      }
    },
    de: {
      homeHref: '/de',
      brandText: 'Danshaker ist die originale, in Dänemark entwickelte und hergestellte Entmistungs- und Sortiermaschine. Wir helfen Ihnen, Zeit und Einstreu zu sparen und den Körper bei der täglichen Stallarbeit zu entlasten.',
      col1Title: 'Die Maschine',
      col2Title: 'Finanzierung',
      col3Title: 'Über uns',
      col4Title: 'Kontakt',
      demoButton: 'Kostenlose Demo buchen',
      demoHref: '/de/kostenlose-demo',
      copyright: `© ${currentYear} Danshaker. Alle Rechte vorbehalten.`,
      developedBy: 'Entwickelt in Dänemark von',
      links: {
        maskinen: [
          { label: 'Produkt', href: '/de/produkt' },
          { label: 'Zubehör', href: '/de/zubehoer' },
          { label: 'Ersparnisrechner', href: '/de/sparrechner' },
          { label: 'Service', href: '/de/service' }
        ],
        finansiering: [
          { label: 'Miete', href: '/de/miete' },
          { label: 'Gewerbeleasing', href: '/de/gewerbeleasing' },
          { label: 'Privatfinanzierung', href: '/de/privatfinanzierung' }
        ],
        omOs: [
          { label: 'Über Danshaker', href: '/de/ueber-danshaker' },
          { label: 'Termine / Events', href: '/de/uns-treffen' },
          { label: 'Neuigkeiten', href: '/de/neuigkeiten' },
          { label: 'Kundenstimmen', href: '/de/kundenstimmen' },
          { label: 'FAQ', href: '/de/faq' }
        ]
      }
    }
  };

  const footer = FOOTER_DATA[currentLang] || FOOTER_DATA.da;
  const links = footer.links;

  return (
    <footer className="relative border-t border-slate-300 bg-slate-100 text-slate-800">
      {/* Upper Footer */}
      <div className="mx-auto max-w-7xl px-4 py-12 md:px-8">
        <div className="grid gap-12 lg:grid-cols-12">
          {/* Brand Column */}
          <div className="lg:col-span-4 space-y-6">
            <Link to={footer.homeHref} className="flex items-center gap-3">
              <img
                src="https://assets.auxodev.dk/cdn-cgi/image/width=300,quality=85,format=auto/assets/e0cd175c-a113-44c2-9ab9-d86f3c298af2/14d0f8cb-56b7-4a51-8379-6cfdf86af873.png"
                alt="Danshaker logo"
                width="200"
                height="72"
                loading="lazy"
                decoding="async"
                className="h-20 w-auto object-contain"
              />
            </Link>
            <p className="text-sm leading-relaxed text-slate-700 font-medium">
              {footer.brandText}
            </p>
            <div className="flex items-center gap-4 pt-2">
              <a href="#" target="_blank" rel="noopener noreferrer" className="flex h-9 w-9 items-center justify-center rounded-xl bg-slate-200 text-slate-700 transition hover:bg-[#0E67A2] hover:text-white" aria-label="Følg os på Facebook">
                <i className="fa-brands fa-facebook text-lg" aria-hidden="true" />
              </a>
              <a href="#" target="_blank" rel="noopener noreferrer" className="flex h-9 w-9 items-center justify-center rounded-xl bg-slate-200 text-slate-700 transition hover:bg-[#0E67A2] hover:text-white" aria-label="Følg os på Instagram">
                <i className="fa-brands fa-instagram text-lg" aria-hidden="true" />
              </a>
              <a href="#" target="_blank" rel="noopener noreferrer" className="flex h-9 w-9 items-center justify-center rounded-xl bg-slate-200 text-slate-700 transition hover:bg-[#0E67A2] hover:text-white" aria-label="Se os på YouTube">
                <i className="fa-brands fa-youtube text-lg" aria-hidden="true" />
              </a>
            </div>
            <div className="flex items-center gap-2 rounded-xl border border-slate-300 bg-slate-200 px-4 py-2.5 text-xs text-slate-800 w-fit font-semibold">
              <ShieldCheck className="h-4 w-4 text-[#0E67A2]" aria-hidden="true" />
              <span>CVR: 37424765 · Danshaker ApS</span>
            </div>
          </div>

          {/* Links Columns */}
          <div className="grid grid-cols-2 gap-8 sm:grid-cols-3 lg:col-span-5">
            <div>
              <p className="mb-4 text-xs font-bold uppercase tracking-widest text-slate-900">{footer.col1Title}</p>
              <ul className="space-y-2.5 text-sm">
                {links.maskinen.map((link) => (
                  <li key={link.label}>
                    <Link to={link.href} className="transition hover:text-[#0E67A2] font-medium">
                      {link.label}
                    </Link>
                  </li>
                ))}
              </ul>
            </div>

            <div>
              <p className="mb-4 text-xs font-bold uppercase tracking-widest text-slate-900">{footer.col2Title}</p>
              <ul className="space-y-2.5 text-sm">
                {links.finansiering.map((link) => (
                  <li key={link.label}>
                    <Link to={link.href} className="transition hover:text-[#0E67A2] font-medium">
                      {link.label}
                    </Link>
                  </li>
                ))}
              </ul>
            </div>

            <div className="col-span-2 sm:col-span-1">
              <p className="mb-4 text-xs font-bold uppercase tracking-widest text-slate-900">{footer.col3Title}</p>
              <ul className="space-y-2.5 text-sm">
                {links.omOs.map((link) => (
                  <li key={link.label}>
                    <Link to={link.href} className="transition hover:text-[#0E67A2] font-medium">
                      {link.label}
                    </Link>
                  </li>
                ))}
              </ul>
            </div>
          </div>

          {/* Contact Column */}
          <div className="lg:col-span-3 space-y-4">
            <p className="text-xs font-bold uppercase tracking-widest text-slate-900">{footer.col4Title}</p>
            <ul className="space-y-3.5 text-sm">
              <li className="flex items-start gap-3">
                <MapPin className="h-5 w-5 shrink-0 text-[#0E67A2]" aria-hidden="true" />
                <span className="text-slate-800 font-medium">
                  Tebbestrupvej 72,<br />
                  8940 Randers SV
                </span>
              </li>
              <li className="flex items-center gap-3">
                <Phone className="h-5 w-5 shrink-0 text-[#0E67A2]" aria-hidden="true" />
                <a href="tel:25303589" className="text-slate-800 font-medium transition hover:text-[#0E67A2] whitespace-nowrap">
                  +45 2530 3589
                </a>
              </li>
              <li className="flex items-center gap-3">
                <Mail className="h-5 w-5 shrink-0 text-[#0E67A2]" aria-hidden="true" />
                <a href="mailto:info@danshaker.dk" className="text-slate-800 font-medium transition hover:text-[#0E67A2] break-all">
                  info@danshaker.dk
                </a>
              </li>
            </ul>

            <div className="pt-4">
              <Link
                to={footer.demoHref}
                className="inline-flex items-center gap-1.5 rounded-xl bg-slate-200 px-4 py-2.5 text-xs font-bold text-slate-900 transition hover:bg-slate-300 shadow-sm"
              >
                {footer.demoButton}
                <ArrowUpRight className="h-3.5 w-3.5 text-[#0E67A2]" aria-hidden="true" />
              </Link>
            </div>
          </div>
        </div>
      </div>

      {/* Lower Footer */}
      <div className="border-t border-slate-300 bg-slate-200/40 py-6 text-center text-xs text-slate-700 font-medium">
        <div className="mx-auto max-w-7xl px-4 md:px-8 flex flex-col sm:flex-row items-center justify-between gap-4">
          <p>{footer.copyright}</p>
          <p className="flex items-center gap-1">
            {footer.developedBy} <span className="text-slate-900 font-bold">Danshaker</span>
          </p>
        </div>
      </div>
    </footer>
  );
}
